import './login-form.scss';
import { useState, useRef, useCallback, useEffect } from 'react';
import Form, {
    Item,
    Label,
    ButtonItem,
    ButtonOptions,
    RequiredRule
} from 'devextreme-react/form';
import TextBox from 'devextreme-react/text-box';
import Validator, { RequiredRule as ValidatorRequiredRule } from 'devextreme-react/validator';
import LoadIndicator from 'devextreme-react/load-indicator';
import { useAuth } from '../../hooks';
import { alertError, corpName, enums, tokenStorage } from '../../base';
import { captchaService } from '../../api';

const loginValidationGroup = 'loginForm';

interface FormData {
    username?: string;
    password?: string;
    captchaCode?: string;
    longTimeToken?: string;
    remember: boolean;
}

export default function LoginForm() {
    const { signIn, autoSignIn } = useAuth();
    const [loading, setLoading] = useState(false);
    const [captchaRequired, setCaptchaRequired] = useState(false);
    const [captchaId, setCaptchaId] = useState<string>();
    const [captchaImage, setCaptchaImage] = useState<string>();
    const [captchaCode, setCaptchaCode] = useState('');
    const formData = useRef<FormData>({ remember: false });
    const longTimeToken = tokenStorage.longTimeToken;

    if (longTimeToken) {
        formData.current.username = longTimeToken.username;
        formData.current.password = "123456";
        formData.current.longTimeToken = longTimeToken.longTimeToken;
        formData.current.remember = true;
    }

    const refreshCaptcha = useCallback(async () => {
        const captcha = await captchaService.create();
        if (!captcha?.id) {
            return;
        }
        setCaptchaId(captcha.id);
        setCaptchaImage(captcha.imageBase64);
        setCaptchaCode('');
        formData.current.captchaCode = '';
    }, []);

    const ensureCaptcha = useCallback(async () => {
        setCaptchaRequired(true);
        await refreshCaptcha();
    }, [refreshCaptcha]);

    useEffect(() => {
        (async () => {
            // 始终要求验证码时也要展示（不因记住登录而跳过）
            const status = await captchaService.isRequired();
            if (status?.required) {
                await ensureCaptcha();
            }
        })();
    }, [ensureCaptcha]);
    
    const onSubmit = useCallback(async (e) => {
        e.preventDefault();
        const { username, password, remember, captchaCode: code } = formData.current;

        setLoading(true);

        const currentLongTimeToken = tokenStorage.longTimeToken;

        const result = currentLongTimeToken?.username === username ?
            await autoSignIn!() :
            await signIn!(username!, password!, remember, captchaId, code);
            
        setLoading(false);

        if (!result.succeeded) {
            alertError(result.message!);
            if (result.signals && result.signals[enums.signalTypes.requireCaptcha]) {
                await ensureCaptcha();
            } else if (captchaRequired) {
                await refreshCaptcha();
            }
        }
    }, [signIn, autoSignIn, captchaId, captchaRequired, ensureCaptcha, refreshCaptcha]);

    return (
        <form className={'login-form'} onSubmit={onSubmit}>
            <Form
                formData={formData.current}
                disabled={loading}
                validationGroup={loginValidationGroup}
            >
                <Item
                    dataField={'username'}
                    editorType={'dxTextBox'}
                    editorOptions={usernameEditorOptions}
                >
                    <RequiredRule message="用户名不能为空" />
                    <Label text="用户名" />
                </Item>
                <Item
                    dataField={'password'}
                    editorType={'dxTextBox'}
                    editorOptions={passwordEditorOptions}
                >
                    <RequiredRule message="密码不能为空" />
                    <Label text="密码" /> 
                </Item>
                {captchaRequired && (
                    <Item dataField={'captchaCode'}>
                        <Label text="验证码" />
                        <div className="login-captcha-row">
                            <TextBox
                                className="login-captcha-input"
                                stylingMode="outlined"
                                disabled={loading}
                                value={captchaCode}
                                valueChangeEvent="keyup input change"
                                onValueChanged={(e) => {
                                    const value = e.value || '';
                                    setCaptchaCode(value);
                                    formData.current.captchaCode = value;
                                }}
                            >
                                <Validator validationGroup={loginValidationGroup}>
                                    <ValidatorRequiredRule message="验证码不能为空" />
                                </Validator>
                            </TextBox>
                            <div
                                className="login-captcha-image"
                                onClick={refreshCaptcha}
                                title="点击刷新验证码"
                            >
                                {captchaImage && (
                                    <img src={`data:image/png;base64,${captchaImage}`} alt="验证码" />
                                )}
                            </div>
                        </div>
                    </Item>
                )}
                <Item dataField={'remember'}
                    editorType={'dxCheckBox'}
                    editorOptions={{'text': '记住我（请勿在公共场所选择此项）'}}>
                    <Label visible={false} />
                </Item>
                <ButtonItem>
                    <ButtonOptions
                        width={'100%'}
                        type={'default'}
                        useSubmitBehavior={true}
                        validationGroup={loginValidationGroup}
                    >
                        <span className="dx-button-text">
                            {
                                loading
                                    ? <LoadIndicator width={'24px'} height={'24px'} visible={true} />
                                    : '登录'
                            }
                        </span>
                    </ButtonOptions>
                </ButtonItem>
                <Item>
                    <div className={'login-about'}>
                        {corpName}
                    </div>
                </Item>
            </Form>
        </form>
    );
}

const usernameEditorOptions = { stylingMode: 'outlined', mode: 'text' };
const passwordEditorOptions = { stylingMode: 'outlined', mode: 'password' };
